import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { setLocale } from "@/lib/i18n";
import { Palette, Globe, Image as ImageIcon, Upload, Trash2 } from "lucide-react";

const PRESET_COLORS = ["#FF5A00", "#10B981", "#3B82F6", "#A855F7", "#EAB308", "#EC4899", "#F97316", "#06B6D4"];

export default function Branding() {
    const { t, i18n } = useTranslation();
    const { workspace, refreshWorkspace } = useAuth();
    const [form, setForm] = useState({
        logo_url: "",
        favicon_url: "",
        accent_color: "#FF5A00",
        sender_name: "Orchestrator Agent",
        sender_signature: "",
        locale: "nl",
        white_label: false,
        brand_name: "",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState("");  // "" | "logo" | "favicon"
    const fileRef = useRef(null);
    const faviconRef = useRef(null);

    useEffect(() => {
        api.get("/branding").then((r) => {
            const b = r.data.branding || {};
            setForm({
                logo_url: b.logo_url || "",
                favicon_url: b.favicon_url || "",
                accent_color: b.accent_color || "#FF5A00",
                sender_name: b.sender_name || "Orchestrator Agent",
                sender_signature: b.sender_signature || "",
                locale: r.data.locale || "nl",
                white_label: !!b.white_label,
                brand_name: b.brand_name || "",
            });
        });
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Generic asset upload used by both logo and favicon.
    const onAssetFile = (kind, urlKey, maxKB) => async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";  // allow re-selecting the same file
        if (!file) return;
        if (file.size > maxKB * 1024) { toast.error(t(`branding.${kind}_too_big`)); return; }
        setUploading(kind);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/branding/${kind}`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
            });
            setForm((f) => ({ ...f, [urlKey]: r.data[urlKey] }));
            toast.success(t(`branding.${kind}_uploaded`));
            refreshWorkspace();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t(`branding.${kind}_upload_failed`));
        } finally { setUploading(""); }
    };

    const removeAsset = (kind, urlKey) => async () => {
        setUploading(kind);
        try {
            await api.delete(`/branding/${kind}`);
            setForm((f) => ({ ...f, [urlKey]: "" }));
            toast.success(t(`branding.${kind}_removed`));
            refreshWorkspace();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t(`branding.${kind}_upload_failed`));
        } finally { setUploading(""); }
    };

    const onLogoFile = onAssetFile("logo", "logo_url", 2048);
    const removeLogo = removeAsset("logo", "logo_url");
    const onFaviconFile = onAssetFile("favicon", "favicon_url", 512);
    const removeFavicon = removeAsset("favicon", "favicon_url");

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/branding", form);
            setLocale(form.locale);
            i18n.changeLanguage(form.locale);
            toast.success(t("branding.saved"));
            // apply accent to CSS variable live
            document.documentElement.style.setProperty("--jr-brand", form.accent_color);
            refreshWorkspace();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("companies.save_failed")); }
        finally { setSaving(false); }
    };

    return (
        <PageHeader title={t("pages.branding_title")} subtitle={t("pages.branding_subtitle")}>
            <div className="grid lg:grid-cols-[1fr_400px] gap-6">
                <form onSubmit={save} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6 space-y-5" data-testid="branding-form">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1"><ImageIcon size={11}/> {t("branding.logo_url")}</label>
                        <div className="mt-2 flex items-center gap-3">
                            {form.logo_url ? (
                                <img src={form.logo_url} alt="logo preview" data-testid="branding-logo-preview"
                                     className="w-11 h-11 rounded-lg object-contain bg-white/90 p-1 border border-white/10 shrink-0"
                                     onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                            ) : (
                                <div className="w-11 h-11 rounded-lg bg-[var(--jr-surface-2)] border border-dashed border-white/15 flex items-center justify-center shrink-0">
                                    <ImageIcon size={16} className="text-[var(--jr-text-3)]"/>
                                </div>
                            )}
                            <input value={form.logo_url} onChange={set("logo_url")} data-testid="branding-logo" placeholder="https://yourcompany.com/logo.png" className="flex-1 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                                   onChange={onLogoFile} className="hidden" data-testid="branding-logo-file-input"/>
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={!!uploading}
                                    data-testid="branding-logo-upload-btn"
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 transition-colors disabled:opacity-50">
                                <Upload size={12}/> {uploading === "logo" ? t("branding.logo_uploading") : t("branding.logo_upload")}
                            </button>
                            {form.logo_url && (
                                <button type="button" onClick={removeLogo} disabled={!!uploading}
                                        data-testid="branding-logo-remove-btn"
                                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-[var(--jr-text-3)] border border-white/10 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50">
                                    <Trash2 size={12}/> {t("branding.logo_remove")}
                                </button>
                            )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-[var(--jr-text-3)]">{t("branding.logo_hint")}</p>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1"><Globe size={11}/> {t("branding.favicon")}</label>
                        <div className="mt-2 flex items-center gap-3">
                            {form.favicon_url ? (
                                <img src={form.favicon_url} alt="favicon preview" data-testid="branding-favicon-preview"
                                     className="w-8 h-8 rounded-md object-contain bg-white/90 p-0.5 border border-white/10 shrink-0"
                                     onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                            ) : (
                                <div className="w-8 h-8 rounded-md bg-[var(--jr-surface-2)] border border-dashed border-white/15 flex items-center justify-center shrink-0">
                                    <Globe size={13} className="text-[var(--jr-text-3)]"/>
                                </div>
                            )}
                            <input ref={faviconRef} type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,.ico,image/jpeg,image/webp,image/gif"
                                   onChange={onFaviconFile} className="hidden" data-testid="branding-favicon-file-input"/>
                            <button type="button" onClick={() => faviconRef.current?.click()} disabled={!!uploading}
                                    data-testid="branding-favicon-upload-btn"
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 transition-colors disabled:opacity-50">
                                <Upload size={12}/> {uploading === "favicon" ? t("branding.favicon_uploading") : t("branding.favicon_upload")}
                            </button>
                            {form.favicon_url && (
                                <button type="button" onClick={removeFavicon} disabled={!!uploading}
                                        data-testid="branding-favicon-remove-btn"
                                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-[var(--jr-text-3)] border border-white/10 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50">
                                    <Trash2 size={12}/> {t("branding.favicon_remove")}
                                </button>
                            )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-[var(--jr-text-3)]">{t("branding.favicon_hint")}</p>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1"><Palette size={11}/> {t("branding.accent_color")}</label>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <input type="color" value={form.accent_color} onChange={set("accent_color")} data-testid="branding-color-picker" className="w-12 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer"/>
                            <input type="text" value={form.accent_color} onChange={set("accent_color")} className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm w-32 font-mono"/>
                            <div className="flex gap-1 flex-wrap">
                                {PRESET_COLORS.map((c) => (
                                    <button type="button" key={c} onClick={() => setForm({...form, accent_color: c})} data-testid={`color-preset-${c}`}
                                        className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                                        style={{ backgroundColor: c, borderColor: form.accent_color === c ? "white" : "transparent" }}/>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("branding.sender_name")}</label>
                        <input value={form.sender_name} onChange={set("sender_name")} data-testid="branding-sender-name" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("branding.sender_signature")}</label>
                        <textarea value={form.sender_signature} onChange={set("sender_signature")} rows={3} data-testid="branding-signature" placeholder="— Jeroen, Senior Recruiter @ Mosaic Recruiting" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)]/50 p-4 space-y-3" data-testid="branding-whitelabel-block">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input type="checkbox" checked={form.white_label}
                                onChange={(e) => setForm({ ...form, white_label: e.target.checked })}
                                data-testid="branding-whitelabel-toggle" className="mt-0.5" />
                            <span>
                                <span className="text-sm text-white font-medium">{t("branding.white_label")}</span>
                                <span className="block text-[11px] text-[var(--jr-text-3)] mt-0.5">{t("branding.white_label_hint")}</span>
                            </span>
                        </label>
                        {form.white_label && (
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("branding.brand_name")}</label>
                                <input value={form.brand_name} onChange={set("brand_name")} data-testid="branding-brand-name"
                                    placeholder={t("branding.brand_name_ph")}
                                    className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1"><Globe size={11}/> {t("branding.locale")}</label>
                        <select value={form.locale} onChange={set("locale")} data-testid="branding-locale" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                            <option value="nl">Nederlands</option>
                            <option value="en">English</option>
                            <option value="fr">Français</option>
                        </select>
                    </div>
                    <button type="submit" disabled={saving} data-testid="branding-save" className="px-5 py-2.5 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {saving ? t("common.saving") : t("common.save")}
                    </button>
                </form>

                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-3 font-mono">{t("branding.preview")}</div>
                    <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
                        <div className="p-5 border-b border-white/8 flex items-center gap-3">
                            {form.logo_url ? <img src={form.logo_url} alt="logo" className="w-9 h-9 rounded-md object-cover border border-white/10"/> :
                                <div className="w-9 h-9 rounded-md grid place-items-center font-display font-bold text-black" style={{ background: form.accent_color }}>
                                    {(workspace?.name || "Demo").trim().charAt(0).toUpperCase()}
                                </div>}
                            <div className="font-display text-lg tracking-tight">{(form.white_label && form.brand_name.trim()) || workspace?.name || "Demo Agency"}</div>
                        </div>
                        <div className="p-5 space-y-3">
                            <button type="button" style={{ background: form.accent_color }} className="px-4 py-2 rounded-full text-white text-sm font-medium">Voorbeeld knop</button>
                            <div className="rounded-lg border border-white/10 p-3 bg-[var(--jr-surface-2)]">
                                <div className="text-xs font-mono uppercase tracking-[0.16em]" style={{ color: form.accent_color }}>Master Agent</div>
                                <p className="text-sm text-[var(--jr-text-2)] mt-1">&ldquo;Hi {form.sender_name?.split(' ')[0] || 'jij'} — 3 nieuwe matches wachten op approval.&rdquo;</p>
                            </div>
                            <div className="rounded-lg border border-white/10 p-3 bg-[var(--jr-surface-2)] text-sm text-[var(--jr-text-2)]">
                                <p>Hi {'{first_name}'},</p>
                                <p className="mt-2">{form.sender_signature || "—"}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageHeader>
    );
}
